import { z } from 'zod';
import { dayMonth, WEEKDAY_LONG, type Locale } from './i18n';
import { addDays, daysBetween, weekdayOf } from './schedule';

/**
 * "Freizeit" (Release 1.10): the nice things that get lost in everyday life – trip ideas, the
 * paper vouchers in the drawer and everybody's wish lists. Firestore: households/{hid}/ideas,
 * /vouchers, /wishes and /wishClaims (who gets a wish – parents only, see the rules).
 *
 * The app reminds quietly: one "Gut zu wissen" line on "Heute" at most, never a push.
 */

const id = z.string().min(1).max(128);
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const millis = z.number().int().nonnegative();
const short = (max: number) => z.string().trim().min(1).max(max);

// ---------------------------------------------------------------------------------------------
// Ideas ("Ideen-Parkplatz")
// ---------------------------------------------------------------------------------------------

/** out = needs dry weather, in = good when it rains, any = both. */
export const IdeaPlace = z.enum(['out', 'in', 'any']);
export type IdeaPlace = z.infer<typeof IdeaPlace>;
export const IdeaLength = z.enum(['short', 'half', 'day']);
export type IdeaLength = z.infer<typeof IdeaLength>;

/**
 * Where an idea is – found on OpenStreetMap by its name (free, no account). Only the idea's name
 * and the town's rough position leave the device for the search.
 */
export const Spot = z.object({
  name: short(120),
  /** "Wildpark 1, Hanstedt" */
  address: z.string().trim().max(160).optional(),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  /** OpenStreetMap object, e.g. "W484314093" – to look it up again */
  osm: z.string().max(32).optional(),
  website: z.string().url().max(300).optional(),
  phone: z.string().max(40).optional(),
  /** OpenStreetMap opening_hours, e.g. "Mo-Su 09:00-18:00" */
  hours: z.string().max(200).optional(),
});
export type Spot = z.infer<typeof Spot>;

export const Idea = z.object({
  id,
  title: short(80),
  place: IdeaPlace.default('any'),
  length: IdeaLength.optional(),
  /** "kostenlos", "ca. 60 €" */
  cost: z.string().trim().max(30).optional(),
  note: z.string().trim().max(280).optional(),
  /** The place itself, when it was found on the map. */
  spot: Spot.optional(),
  /** Who wants to go ("♥ Ich auch") – member ids. */
  hearts: z.array(id).max(20).default([]),
  /** Planned for this day (a calendar entry exists); in the past = "Erlebt". */
  planned: IsoDate.optional(),
  createdBy: z.string().optional(),
  createdAt: millis,
});
export type Idea = z.infer<typeof Idea>;

/** Texts of Freizeit that the server and the app share (German master, English 1.19). */
export const LEISURE_TEXT = {
  de: {
    place: {
      out: 'draußen',
      in: 'drinnen · auch bei Regen',
      any: 'drinnen oder draußen',
    } satisfies Record<IdeaPlace, string>,
    length: { short: 'kurz', half: 'halber Tag', day: 'ganzer Tag' } satisfies Record<
      IdeaLength,
      string
    >,
    occasion: { birthday: 'Geburtstag', christmas: 'Weihnachten', any: 'einfach so' },
    near: 'ganz in der Nähe',
    minutes: (n: string) => `${n} Min.`,
    hours: (n: string) => `${n} Std.`,
    byCar: (km: string, time: string) => `ca. ${km} km · etwa ${time} mit dem Auto`,
    open247: 'rund um die Uhr',
    closed: 'geschlossen',
    ohDays: {
      Mo: 'Mo',
      Tu: 'Di',
      We: 'Mi',
      Th: 'Do',
      Fr: 'Fr',
      Sa: 'Sa',
      Su: 'So',
      PH: 'Feiertage',
      SH: 'Schulferien',
    },
    months: { Mar: 'Mär', May: 'Mai', Oct: 'Okt', Dec: 'Dez' },
    expired: 'abgelaufen',
    lastDay: 'nur noch heute',
    untilTomorrow: 'noch bis morgen',
    daysLeft: (n: string) => `noch ${n} Tage`,
    until: (date: string) => `bis ${date}`,
    runsOutToday: 'läuft heute ab',
    runsOutTomorrow: 'läuft morgen ab',
    runsOutIn: (n: string) => `läuft in ${n} Tagen ab`,
    voucherDue: (voucher: string, when: string) => `Gutschein ${voucher} ${when}`,
    keptIn: (place: string) => ` – liegt in: ${place}`,
    today: 'Heute',
    rainy: ', Regen angesagt',
    dry: ' und trocken',
    weekendVoucher: (day: string, rain: string, voucher: string, until: string) =>
      `${day} ist frei${rain} – Gutschein ${voucher} einlösen? Gilt bis ${until}`,
    weekendIdea: (day: string, weather: string, idea: string) =>
      `${day} frei${weather} – wie wär's mit ${idea}?`,
  },
  en: {
    place: {
      out: 'outdoors',
      in: 'indoors · good when it rains',
      any: 'indoors or outdoors',
    } satisfies Record<IdeaPlace, string>,
    length: { short: 'short', half: 'half a day', day: 'whole day' } satisfies Record<
      IdeaLength,
      string
    >,
    occasion: { birthday: 'Birthday', christmas: 'Christmas', any: 'just because' },
    near: 'very close by',
    minutes: (n: string) => `${n} min`,
    hours: (n: string) => `${n} hr`,
    byCar: (km: string, time: string) => `about ${km} km · roughly ${time} by car`,
    open247: 'open 24 hours',
    closed: 'closed',
    ohDays: {
      Mo: 'Mon',
      Tu: 'Tue',
      We: 'Wed',
      Th: 'Thu',
      Fr: 'Fri',
      Sa: 'Sat',
      Su: 'Sun',
      PH: 'bank holidays',
      SH: 'school holidays',
    },
    months: { Mar: 'Mar', May: 'May', Oct: 'Oct', Dec: 'Dec' },
    expired: 'expired',
    lastDay: 'today only',
    untilTomorrow: 'until tomorrow',
    daysLeft: (n: string) => `${n} days left`,
    until: (date: string) => `until ${date}`,
    runsOutToday: 'runs out today',
    runsOutTomorrow: 'runs out tomorrow',
    runsOutIn: (n: string) => `runs out in ${n} days`,
    voucherDue: (voucher: string, when: string) => `Voucher ${voucher} ${when}`,
    keptIn: (place: string) => ` – kept in: ${place}`,
    today: 'Today',
    rainy: ', rain forecast',
    dry: ' and dry',
    weekendVoucher: (day: string, rain: string, voucher: string, until: string) =>
      `${day} is free${rain} – use the voucher ${voucher}? Valid until ${until}`,
    weekendIdea: (day: string, weather: string, idea: string) =>
      `${day} is free${weather} – how about ${idea}?`,
  },
} as const;

/** Straight-line distance in km (haversine). */
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/**
 * "ca. 190 km · gut 2 Std. mit dem Auto" – a rough guess (roads are ~30 % longer than the
 * straight line, ~70 km/h on average), rounded so it never pretends to be exact. The real route
 * is one tap away in Google Maps.
 */
export function distanceLabel(km: number, locale: Locale = 'de'): string {
  const t = LEISURE_TEXT[locale];
  if (km < 1) return t.near;
  const road = km * 1.3;
  const min = Math.max(5, Math.round(((road / 70) * 60) / 5) * 5);
  const shown = km < 10 ? Math.round(km) : Math.round(km / 5) * 5;
  const time =
    min < 60
      ? t.minutes(String(min))
      : t.hours(String(Math.floor(min / 60))) + (min % 60 ? ` ${t.minutes(String(min % 60))}` : '');
  return t.byCar(String(shown), time);
}

/** OpenStreetMap opening hours in German words ("Mo-Su 09:00-18:00" → "Mo–So 09:00–18:00"). */
export function hoursLabel(oh: string, locale: Locale = 'de'): string {
  const t = LEISURE_TEXT[locale];
  if (oh.trim() === '24/7') return t.open247;
  return oh
    .replace(/\b(Mo|Tu|We|Th|Fr|Sa|Su|PH|SH)\b/g, (d) => t.ohDays[d as keyof typeof t.ohDays])
    .replace(/\b(off|closed)\b/g, t.closed)
    .replace(/\b(Mar|May|Oct|Dec)\b/g, (m) => t.months[m as keyof typeof t.months])
    .replace(/(\d|[A-Za-zäÄ]{2,})-(\d|[A-Za-zäÄ]{2,})/g, '$1–$2')
    .replace(/\s*;\s*/g, ' · ');
}

/**
 * Outdoors or indoors, from what OpenStreetMap says the place is. Unknown → 'any' (the family
 * can still change it).
 */
export function placeOf(key?: string, value?: string): IdeaPlace {
  const out = [
    'zoo',
    'theme_park',
    'park',
    'nature_reserve',
    'viewpoint',
    'playground',
    'garden',
    'picnic_site',
    'beach_resort',
    'miniature_golf',
    'camp_site',
    'attraction',
    'peak',
    'beach',
    'water',
    'forest',
    'wood',
  ];
  const inside = [
    'museum',
    'cinema',
    'theatre',
    'bowling_alley',
    'sports_centre',
    'ice_rink',
    'aquarium',
    'planetarium',
    'library',
    'escape_game',
    'trampoline_park',
    'amusement_arcade',
    'gallery',
  ];
  if (value && inside.includes(value)) return 'in';
  if (value && out.includes(value)) return 'out';
  if (key === 'natural') return 'out';
  return 'any';
}

/** Route in Google Maps (opens the app on phones) – no account, no key. */
export const routeLink = (s: Pick<Spot, 'lat' | 'lon'>) =>
  `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}`;

// ---------------------------------------------------------------------------------------------
// Vouchers – the real ones from the drawer (nothing to do with stars)
// ---------------------------------------------------------------------------------------------

export const Voucher = z.object({
  id,
  /** What and where: "Therme am Stadtpark" */
  title: short(80),
  /** "25 €", "2 Personen" */
  value: z.string().trim().max(40).optional(),
  /** Valid until (last day); missing = no end date. */
  until: IsoDate.optional(),
  code: z.string().trim().max(60).optional(),
  /** Where the paper is: "Schublade Flur" */
  place: z.string().trim().max(60).optional(),
  /** From whom: "Oma" */
  from: z.string().trim().max(40).optional(),
  /** For whom: 'all' or member ids. */
  forWhom: z.union([z.literal('all'), z.array(id).min(1).max(20)]).default('all'),
  note: z.string().trim().max(280).optional(),
  used: z.boolean().default(false),
  usedAt: millis.optional(),
  /** "Nicht mehr erinnern" – stays in the list, no more hints. */
  quiet: z.boolean().optional(),
  createdBy: z.string().optional(),
  createdAt: millis,
});
export type Voucher = z.infer<typeof Voucher>;

/** Days until the voucher runs out (0 = last day today, negative = expired). */
export const daysLeft = (today: string, until: string) => daysBetween(today, until);

/** Open vouchers first by expiry (no end date last), then by name. */
export function sortVouchers<T extends Pick<Voucher, 'until' | 'title'>>(list: T[]): T[] {
  return [...list].sort(
    (a, b) =>
      (a.until ?? '9999-12-31').localeCompare(b.until ?? '9999-12-31') ||
      a.title.localeCompare(b.title, 'de'),
  );
}

/**
 * The quiet reminder: 60, 14 and 3 days before the end – each time for a few days, so it is not
 * missed on a busy day, and gone again before it nags. Returns the days left, or null.
 */
export const REMIND_WINDOWS: [from: number, to: number][] = [
  [60, 56],
  [14, 11],
  [3, 0],
];
export function voucherReminder(
  today: string,
  v: Pick<Voucher, 'until' | 'used' | 'quiet'>,
): number | null {
  if (!v.until || v.used || v.quiet) return null;
  const left = daysLeft(today, v.until);
  return REMIND_WINDOWS.some(([from, to]) => left <= from && left >= to) ? left : null;
}

/** "noch 12 Tage", "nur noch heute", "abgelaufen" */
export function leftLabel(today: string, until: string, locale: Locale = 'de'): string {
  const t = LEISURE_TEXT[locale];
  const left = daysLeft(today, until);
  if (left < 0) return t.expired;
  if (left === 0) return t.lastDay;
  if (left === 1) return t.untilTomorrow;
  if (left <= 31) return t.daysLeft(String(left));
  const year = until.slice(0, 4) !== today.slice(0, 4) ? until.slice(0, 4) : '';
  return t.until(
    locale === 'de' ? dayMonth(until, locale) + year : `${dayMonth(until, locale)} ${year}`.trim(),
  );
}

// ---------------------------------------------------------------------------------------------
// Wish lists – for everybody, plus the family's shared wishes
// ---------------------------------------------------------------------------------------------

export const FAMILY_WISHES = 'family';

export const Wish = z.object({
  id,
  /** member id, or 'family' for the shared wishes (Trampolin) */
  owner: id,
  title: short(80),
  /** "ca. 250 €" */
  price: z.string().trim().max(30).optional(),
  link: z.string().trim().url().max(500).optional(),
  /** 1–3 hearts: 3 = Herzenswunsch */
  love: z.number().int().min(1).max(3).default(1),
  occasion: z.enum(['birthday', 'christmas', 'any']).default('any'),
  /** Got it – stays visible as "Bekommen" for a while. */
  received: z.boolean().optional(),
  createdBy: z.string().optional(),
  createdAt: millis,
});
export type Wish = z.infer<typeof Wish>;

/**
 * "Ich besorge das" (document id = wish id). A separate collection, because the rules can hide a
 * whole document but not single fields: only adults read it – never kids or teens, and the wall
 * only with the parent PIN. On their own list nobody sees it (the app leaves it out).
 */
export const WishClaim = z.object({
  /** the adults who get it ("Zusammen schenken" = several) */
  by: z.array(id).min(1).max(8),
  createdAt: millis,
});
export type WishClaim = z.infer<typeof WishClaim>;

/** May this viewer see who gets the wish? Adults only, and never on their own list. */
export const seesClaims = (viewer: { adult: boolean; memberId?: string }, owner: string) =>
  viewer.adult && owner !== viewer.memberId;

// ---------------------------------------------------------------------------------------------
// "Denkt mit": at most one leisure hint a day
// ---------------------------------------------------------------------------------------------

export interface LeisureDay {
  /** YYYY-MM-DD */
  date: string;
  /** nothing in the calendar for the family (school, bins and series excluded) */
  free: boolean;
  /** weather, if known */
  rain?: number;
  max?: number;
}

export interface LeisureHint {
  id: string;
  kind: 'voucher' | 'idea';
  text: string;
  href: string;
}

/** A day with this chance of rain is a day for indoor plans. */
export const RAINY = 60;
/** A voucher running out within this many days is worth a free weekend. */
const VOUCHER_SOON = 60;

const withValue = (v: Pick<Voucher, 'title' | 'value'>) =>
  v.value ? `${v.title} (${v.value})` : v.title;

/**
 * The one leisure hint for today, or nothing:
 *  1. a voucher in its reminder window (60 / 14 / 3 days) – the earliest first
 *  2. from Thursday on: a free weekend day ahead → a voucher that runs out soon, or the idea
 *     with the most hearts that fits the weather (rain → indoors)
 * Weekend plans need a free day; without weather the idea must work indoors and out.
 */
export function leisureHint(input: {
  today: string;
  vouchers: Pick<Voucher, 'id' | 'title' | 'value' | 'until' | 'place' | 'used' | 'quiet'>[];
  ideas: Pick<Idea, 'id' | 'title' | 'place' | 'hearts' | 'planned'>[];
  /** the next days (today first) – only Saturday and Sunday ahead are looked at */
  days: LeisureDay[];
  locale?: Locale;
}): LeisureHint | null {
  const { today, vouchers, ideas, days, locale = 'de' } = input;
  const t = LEISURE_TEXT[locale];

  const due = sortVouchers(vouchers.filter((v) => voucherReminder(today, v) !== null))[0];
  if (due?.until) {
    const left = daysLeft(today, due.until);
    const when =
      left === 0 ? t.runsOutToday : left === 1 ? t.runsOutTomorrow : t.runsOutIn(String(left));
    return {
      id: `voucher:${due.id}:${due.until}`,
      kind: 'voucher',
      text: t.voucherDue(withValue(due), when) + (due.place ? t.keptIn(due.place) : ''),
      href: '/freizeit/gutscheine',
    };
  }

  const wd = weekdayOf(today);
  if (wd < 4) return null;
  const weekend = days.find(
    (d) => d.date >= today && weekdayOf(d.date) >= 6 && daysBetween(today, d.date) <= 3 && d.free,
  );
  if (!weekend) return null;
  const name =
    weekend.date === today ? t.today : WEEKDAY_LONG[locale][weekdayOf(weekend.date) - 1]!;
  const rainy = weekend.rain !== undefined && weekend.rain >= RAINY;
  const known = weekend.rain !== undefined;

  const soon = sortVouchers(
    vouchers.filter(
      (v) =>
        !v.used &&
        v.until &&
        daysLeft(today, v.until) >= daysBetween(today, weekend.date) &&
        daysLeft(today, v.until) <= VOUCHER_SOON,
    ),
  )[0];
  if (soon?.until) {
    return {
      id: `weekend-voucher:${soon.id}:${weekend.date}`,
      kind: 'voucher',
      text: t.weekendVoucher(
        name,
        rainy ? t.rainy : '',
        withValue(soon),
        dayMonth(soon.until, locale),
      ),
      href: '/freizeit/gutscheine',
    };
  }

  const fits = (p: IdeaPlace) => (!known ? p === 'any' : rainy ? p !== 'out' : p !== 'in');
  const idea = ideas
    .filter((i) => (!i.planned || i.planned < addDays(today, -30)) && fits(i.place))
    .sort((a, b) => b.hearts.length - a.hearts.length || a.title.localeCompare(b.title, 'de'))[0];
  if (!idea) return null;
  const weather = !known ? '' : rainy ? t.rainy : t.dry;
  const hearts = idea.hearts.length ? ` (${idea.hearts.length} ♥)` : '';
  return {
    id: `weekend-idea:${idea.id}:${weekend.date}`,
    kind: 'idea',
    text: t.weekendIdea(name, weather, idea.title) + hearts,
    href: '/freizeit/ideen',
  };
}
